/**
 * Tiny matrix + mesh toolkit for the homepage lab machine. Column-major 4x4
 * matrices (the layout WebGL expects) and builders that emit interleaved
 * vertex data: position (3), normal (3), glow (2: amount, blink seed).
 * Model matrices only ever rotate and translate, so normals transform with
 * the same matrix.
 */

export type V3 = [number, number, number]
export type M4 = Float32Array

export const FLOATS_PER_VERTEX = 8

export function identity(): M4 {
  const m = new Float32Array(16)
  m[0] = m[5] = m[10] = m[15] = 1
  return m
}

export function multiply(a: M4, b: M4): M4 {
  const o = new Float32Array(16)
  for (let c = 0; c < 4; c++) {
    for (let r = 0; r < 4; r++) {
      let s = 0
      for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k]
      o[c * 4 + r] = s
    }
  }
  return o
}

export function chain(...ms: M4[]): M4 {
  return ms.reduce((acc, m) => multiply(acc, m), identity())
}

export function translate(x: number, y: number, z: number): M4 {
  const m = identity()
  m[12] = x
  m[13] = y
  m[14] = z
  return m
}

export function rotateX(a: number): M4 {
  const c = Math.cos(a)
  const s = Math.sin(a)
  const m = identity()
  m[5] = c
  m[6] = s
  m[9] = -s
  m[10] = c
  return m
}

export function rotateY(a: number): M4 {
  const c = Math.cos(a)
  const s = Math.sin(a)
  const m = identity()
  m[0] = c
  m[2] = -s
  m[8] = s
  m[10] = c
  return m
}

export function rotateZ(a: number): M4 {
  const c = Math.cos(a)
  const s = Math.sin(a)
  const m = identity()
  m[0] = c
  m[1] = s
  m[4] = -s
  m[5] = c
  return m
}

export function perspective(fovy: number, aspect: number, near: number, far: number): M4 {
  const f = 1 / Math.tan(fovy / 2)
  const m = new Float32Array(16)
  m[0] = f / aspect
  m[5] = f
  m[10] = (far + near) / (near - far)
  m[11] = -1
  m[14] = (2 * far * near) / (near - far)
  return m
}

export function applyPoint(m: M4, p: V3): V3 {
  return [
    m[0] * p[0] + m[4] * p[1] + m[8] * p[2] + m[12],
    m[1] * p[0] + m[5] * p[1] + m[9] * p[2] + m[13],
    m[2] * p[0] + m[6] * p[1] + m[10] * p[2] + m[14],
  ]
}

export function applyDir(m: M4, v: V3): V3 {
  const x = m[0] * v[0] + m[4] * v[1] + m[8] * v[2]
  const y = m[1] * v[0] + m[5] * v[1] + m[9] * v[2]
  const z = m[2] * v[0] + m[6] * v[1] + m[10] * v[2]
  const l = Math.hypot(x, y, z) || 1
  return [x / l, y / l, z / l]
}

export function mix3(a: V3, b: V3, t: number): V3 {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]
}

export function scale3(a: V3, s: number): V3 {
  return [a[0] * s, a[1] * s, a[2] * s]
}

/** A growable triangle soup. */
export class Geo {
  data: number[] = []

  vert(p: V3, n: V3, glow = 0, seed = 0) {
    this.data.push(p[0], p[1], p[2], n[0], n[1], n[2], glow, seed)
  }

  quad(a: V3, b: V3, c: V3, d: V3, n: V3, glow = 0, seed = 0) {
    this.vert(a, n, glow, seed)
    this.vert(b, n, glow, seed)
    this.vert(c, n, glow, seed)
    this.vert(a, n, glow, seed)
    this.vert(c, n, glow, seed)
    this.vert(d, n, glow, seed)
  }

  /** Append another mesh, optionally moved by a rotate/translate matrix. */
  add(other: Geo, m?: M4): this {
    const d = other.data
    for (let i = 0; i < d.length; i += FLOATS_PER_VERTEX) {
      const p: V3 = [d[i], d[i + 1], d[i + 2]]
      const n: V3 = [d[i + 3], d[i + 4], d[i + 5]]
      const pp = m ? applyPoint(m, p) : p
      const nn = m ? applyDir(m, n) : n
      this.data.push(pp[0], pp[1], pp[2], nn[0], nn[1], nn[2], d[i + 6], d[i + 7])
    }
    return this
  }

  /** Mark every vertex as glowing (emissive) with a blink seed (0 = steady). */
  glow(amount: number, seed = 0): this {
    for (let i = 0; i < this.data.length; i += FLOATS_PER_VERTEX) {
      this.data[i + 6] = amount
      this.data[i + 7] = seed
    }
    return this
  }

  get count() {
    return this.data.length / FLOATS_PER_VERTEX
  }
}

export function box(w: number, h: number, d: number): Geo {
  const x = w / 2
  const y = h / 2
  const z = d / 2
  const g = new Geo()
  g.quad([-x, -y, z], [x, -y, z], [x, y, z], [-x, y, z], [0, 0, 1])
  g.quad([x, -y, -z], [-x, -y, -z], [-x, y, -z], [x, y, -z], [0, 0, -1])
  g.quad([x, -y, z], [x, -y, -z], [x, y, -z], [x, y, z], [1, 0, 0])
  g.quad([-x, -y, -z], [-x, -y, z], [-x, y, z], [-x, y, -z], [-1, 0, 0])
  g.quad([-x, y, z], [x, y, z], [x, y, -z], [-x, y, -z], [0, 1, 0])
  g.quad([-x, -y, -z], [x, -y, -z], [x, -y, z], [-x, -y, z], [0, -1, 0])
  return g
}

/** Cylinder along Y, centred on the origin. */
export function cylinder(r: number, h: number, seg = 24, caps = true): Geo {
  const g = new Geo()
  const y = h / 2
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * Math.PI * 2
    const a1 = ((i + 1) / seg) * Math.PI * 2
    const c0 = Math.cos(a0)
    const s0 = Math.sin(a0)
    const c1 = Math.cos(a1)
    const s1 = Math.sin(a1)
    g.vert([r * c0, -y, r * s0], [c0, 0, s0])
    g.vert([r * c1, -y, r * s1], [c1, 0, s1])
    g.vert([r * c1, y, r * s1], [c1, 0, s1])
    g.vert([r * c0, -y, r * s0], [c0, 0, s0])
    g.vert([r * c1, y, r * s1], [c1, 0, s1])
    g.vert([r * c0, y, r * s0], [c0, 0, s0])
    if (caps) {
      g.vert([0, y, 0], [0, 1, 0])
      g.vert([r * c0, y, r * s0], [0, 1, 0])
      g.vert([r * c1, y, r * s1], [0, 1, 0])
      g.vert([0, -y, 0], [0, -1, 0])
      g.vert([r * c1, -y, r * s1], [0, -1, 0])
      g.vert([r * c0, -y, r * s0], [0, -1, 0])
    }
  }
  return g
}

/** Hollow tube along Y: outer wall, inner wall and both annular caps. */
export function ring(rIn: number, rOut: number, h: number, seg = 32): Geo {
  const g = new Geo()
  const y = h / 2
  for (let i = 0; i < seg; i++) {
    const a0 = (i / seg) * Math.PI * 2
    const a1 = ((i + 1) / seg) * Math.PI * 2
    const c0 = Math.cos(a0)
    const s0 = Math.sin(a0)
    const c1 = Math.cos(a1)
    const s1 = Math.sin(a1)
    // outer wall
    g.vert([rOut * c0, -y, rOut * s0], [c0, 0, s0])
    g.vert([rOut * c1, -y, rOut * s1], [c1, 0, s1])
    g.vert([rOut * c1, y, rOut * s1], [c1, 0, s1])
    g.vert([rOut * c0, -y, rOut * s0], [c0, 0, s0])
    g.vert([rOut * c1, y, rOut * s1], [c1, 0, s1])
    g.vert([rOut * c0, y, rOut * s0], [c0, 0, s0])
    // inner wall
    g.vert([rIn * c0, -y, rIn * s0], [-c0, 0, -s0])
    g.vert([rIn * c1, y, rIn * s1], [-c1, 0, -s1])
    g.vert([rIn * c1, -y, rIn * s1], [-c1, 0, -s1])
    g.vert([rIn * c0, -y, rIn * s0], [-c0, 0, -s0])
    g.vert([rIn * c0, y, rIn * s0], [-c0, 0, -s0])
    g.vert([rIn * c1, y, rIn * s1], [-c1, 0, -s1])
    // caps
    g.quad([rIn * c0, y, rIn * s0], [rOut * c0, y, rOut * s0], [rOut * c1, y, rOut * s1], [rIn * c1, y, rIn * s1], [0, 1, 0])
    g.quad([rIn * c1, -y, rIn * s1], [rOut * c1, -y, rOut * s1], [rOut * c0, -y, rOut * s0], [rIn * c0, -y, rIn * s0], [0, -1, 0])
  }
  return g
}

export function sphere(r: number, wSeg = 16, hSeg = 10): Geo {
  const g = new Geo()
  const point = (i: number, j: number): V3 => {
    const th = (i / wSeg) * Math.PI * 2
    const ph = (j / hSeg) * Math.PI
    return [Math.sin(ph) * Math.cos(th), Math.cos(ph), Math.sin(ph) * Math.sin(th)]
  }
  for (let j = 0; j < hSeg; j++) {
    for (let i = 0; i < wSeg; i++) {
      const n = [point(i, j), point(i + 1, j), point(i + 1, j + 1), point(i, j + 1)]
      const p = n.map((v) => scale3(v, r))
      g.vert(p[0], n[0])
      g.vert(p[1], n[1])
      g.vert(p[2], n[2])
      g.vert(p[0], n[0])
      g.vert(p[2], n[2])
      g.vert(p[3], n[3])
    }
  }
  return g
}

/** Extrude a closed, star-shaped (around the origin) x/z outline along Y. */
export function extrude(outline: [number, number][], h: number): Geo {
  const g = new Geo()
  const y = h / 2
  const n = outline.length
  for (let i = 0; i < n; i++) {
    const [x0, z0] = outline[i]
    const [x1, z1] = outline[(i + 1) % n]
    g.vert([0, y, 0], [0, 1, 0])
    g.vert([x0, y, z0], [0, 1, 0])
    g.vert([x1, y, z1], [0, 1, 0])
    g.vert([0, -y, 0], [0, -1, 0])
    g.vert([x1, -y, z1], [0, -1, 0])
    g.vert([x0, -y, z0], [0, -1, 0])
    let nx = z1 - z0
    let nz = -(x1 - x0)
    const l = Math.hypot(nx, nz) || 1
    nx /= l
    nz /= l
    if (nx * (x0 + x1) + nz * (z0 + z1) < 0) {
      nx = -nx
      nz = -nz
    }
    g.quad([x0, -y, z0], [x1, -y, z1], [x1, y, z1], [x0, y, z0], [nx, 0, nz])
  }
  return g
}

export function gearOutline(teeth: number, rRoot: number, rTip: number): [number, number][] {
  const pts: [number, number][] = []
  const pitch = (Math.PI * 2) / teeth
  for (let i = 0; i < teeth; i++) {
    const a = i * pitch
    for (const [f, r] of [
      [0, rRoot],
      [0.3, rTip],
      [0.55, rTip],
      [0.85, rRoot],
    ] as const) {
      const ang = a + f * pitch
      pts.push([Math.cos(ang) * r, Math.sin(ang) * r])
    }
  }
  return pts
}

/** Turn a Y-axis mesh so its axis points at the viewer (+Z). */
export const FACE_Z = rotateX(Math.PI / 2)

/** Fan rotor in the XY plane: hub plus twisted blades, spinning about Z. */
export function fanRotor(radius: number, blades = 7): Geo {
  const g = new Geo()
  g.add(cylinder(radius * 0.24, 0.14, 20), FACE_Z)
  g.add(sphere(radius * 0.2, 14, 8), translate(0, 0, 0.06))
  const len = radius * 0.78
  const blade = box(len, radius * 0.3, 0.025)
  for (let i = 0; i < blades; i++) {
    g.add(blade, chain(rotateZ((i / blades) * Math.PI * 2), translate(radius * 0.18 + len / 2, 0, 0), rotateX(0.5)))
  }
  return g
}
